/* eslint-disable */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ScrollText, Users } from "lucide-react";
import { Card, Pill } from "../ui";
import { boardApi } from "./api";
import MemberCard from "./MemberCard";
import "./RolePage.css";

// Map a board role onto the closest design-system Pill variant.
// Mirrors the mapping used in MemberCard so the chip color is consistent.
function rolePillVariant(role) {
  switch ((role || "").toUpperCase()) {
    case "PRESIDENT":
    case "VICE_PRESIDENT":
      return "warning";
    case "TREASURER":
    case "JOINT_TREASURER":
      return "success";
    case "SECRETARY":
    case "JOINT_SECRETARY":
      return "p2";
    case "DIRECTOR":
      return "neutral";
    default:
      return "neutral";
  }
}

/**
 * Bye-laws responsibility + current holder(s) + past holders timeline
 * for a single board role. The Board page links here from each member
 * card via the `vsbm-role-link` element.
 */
export default function RolePage() {
  const { role } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    boardApi
      .getRole(role)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Could not load role");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [role]);

  if (loading) {
    return (
      <div className="vsrp-shell">
        <div className="vsrp-empty">Loading…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="vsrp-shell">
        <Link to="/board" className="vsrp-back">
          <ArrowLeft size={14} /> Back to Board
        </Link>
        <div className="vsrp-empty">{error || "Role not found"}</div>
      </div>
    );
  }

  return (
    <div className="vsrp-shell">
      <Link to="/board" className="vsrp-back">
        <ArrowLeft size={14} /> Back to Board
      </Link>

      <header className="vsrp-head">
        <Pill
          variant={rolePillVariant(data.role)}
          className="vsrp-role-pill"
        >
          {data.label}
        </Pill>
        <h1>What does the {data.label} do?</h1>
        <p className="vsrp-sub">
          Per the VSMACS bye-laws ·{" "}
          {data.singleton
            ? "one active holder at a time"
            : `up to ${data.max_active} at a time`}
        </p>
      </header>

      <Card className="vsrp-card vsrp-resp">
        <div className="vsrp-card-head">
          <ScrollText size={16} />
          <h2>Responsibilities</h2>
        </div>
        <p>{data.responsibility}</p>
      </Card>

      <Card className="vsrp-card">
        <div className="vsrp-card-head">
          <Users size={16} />
          <h2>
            Currently {data.singleton ? "held by" : "holding the role"}
          </h2>
        </div>
        {data.current.length === 0 ? (
          <div className="vsrp-empty-inline">
            No one is currently in this role.
          </div>
        ) : (
          <div className="vsrp-cards">
            {data.current.map((m) => (
              <MemberCard
                key={m.id}
                variant="board"
                member={m}
                isAdmin={false}
              />
            ))}
          </div>
        )}
      </Card>

    </div>
  );
}
